import { useSearchParams } from 'react-router-dom';
import { AlertOctagon, Flame, Inbox, ShieldAlert, Sparkles } from 'lucide-react';

import { KpiCard } from '@/components/fleetguardian';
import { MetricKpiCard } from '@/components/shared/MetricKpiCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useFraudHubKpis } from '@/api/hooks/useFraudHubKpis';
import { parsePeriod } from '@/lib/timePeriod';

/**
 * Fraud dashboard KPI row (Wave 2C). Reads the shared `?period=` and renders
 * the five Hub KPIs from `/fraud-hub/kpis` via the standard KpiMetric envelope
 * — `new_in_period` carries a prior-period delta pill (invertDelta: more fraud
 * is worse); the open-status counts are period-independent so they show no
 * pill. Numbers are 100% server-aggregated.
 */
export function FraudKpiRow(): JSX.Element {
  const [searchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'M');
  const { data, isLoading, isError, error } = useFraudHubKpis(period);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[116px] w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load fraud KPIs: {error?.message ?? 'unknown error'}
      </div>
    );
  }

  const m = data.metrics;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
      <MetricKpiCard
        label="Open Critical"
        metric={m.open_critical}
        icon={AlertOctagon}
        hue="red"
        invertDelta
      />
      <MetricKpiCard
        label="Open High"
        metric={m.open_high}
        icon={ShieldAlert}
        hue="orange"
        invertDelta
      />
      <MetricKpiCard
        label="Total Open"
        metric={m.total_open}
        icon={Inbox}
        hue="gray"
        invertDelta
      />
      <MetricKpiCard
        label={`New (${data.period_label})`}
        metric={m.new_in_period}
        icon={Sparkles}
        hue="indigo"
        invertDelta
      />
      <KpiCard
        label="Top Rule"
        icon={Flame}
        hue="amber"
        value={
          data.top_rule_id
            ? data.top_rule_fire_count_24h.toLocaleString('en-ZA')
            : '—'
        }
        subtitle={
          data.top_rule_id
            ? `${data.top_rule_id}${data.top_rule_name ? ` · ${data.top_rule_name}` : ''}`
            : 'no fires this period'
        }
      />
    </div>
  );
}

export default FraudKpiRow;
